// Jadval holati va uning URL ko'rinishi.
//
// NEGA URL'DA (07-FRONTEND.md §9): sahifa yangilanganda filtr yo'qolmaydi,
// "orqaga" tugmasi kutilganday ishlaydi va operator hamkasbiga aynan o'zi
// ko'rgan ro'yxat havolasini yubora oladi ("mana shu 3 ta osilgan sessiya").
// Holat komponent ichida `useState` da tursa, uchalasi ham yo'qoladi.

export type SortOrder = 'ascend' | 'descend'

export interface TableState {
  /** 1 dan boshlanadi — foydalanuvchi ko'radigan raqam bilan bir xil */
  page: number
  pageSize: number
  sortField: string | null
  sortOrder: SortOrder | null
  /** Ustun kaliti → tanlangan qiymatlar. Bo'sh massiv saqlanmaydi. */
  filters: Readonly<Record<string, readonly string[]>>
  search: string
}

export const DEFAULT_PAGE_SIZE = 25
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

export const EMPTY_TABLE_STATE: TableState = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sortField: null,
  sortOrder: null,
  filters: {},
  search: '',
}

/**
 * URL parametr nomlari. Bitta sahifada ikki jadval bo'lsa `prefix` beriladi
 * (`left.page`, `right.page`) — aks holda ular bir-birining holatini
 * o'chirib yuborardi.
 */
export function paramNames(prefix: string) {
  const p = prefix ? `${prefix}.` : ''
  return {
    page: `${p}page`,
    size: `${p}size`,
    sort: `${p}sort`,
    dir: `${p}dir`,
    search: `${p}q`,
    /** Filtrlar `f.<ustun>` shaklida: `?f.status=online,offline` */
    filterPrefix: `${p}f.`,
  }
}

function parsePositiveInt(value: string | null, fallback: number): number {
  if (!value) return fallback
  const n = Number.parseInt(value, 10)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

/** URL → holat. Noto'g'ri qiymat panelni yiqitmaydi, standartga qaytadi. */
export function readTableState(
  params: URLSearchParams,
  prefix: string,
  defaultPageSize: number,
): TableState {
  const names = paramNames(prefix)

  const dir = params.get(names.dir)
  const sortField = params.get(names.sort)

  const filters: Record<string, readonly string[]> = {}
  for (const [key, value] of params.entries()) {
    if (!key.startsWith(names.filterPrefix)) continue
    const column = key.slice(names.filterPrefix.length)
    const values = value.split(',').filter((v) => v !== '')
    if (values.length > 0) filters[column] = values
  }

  return {
    page: parsePositiveInt(params.get(names.page), 1),
    pageSize: parsePositiveInt(params.get(names.size), defaultPageSize),
    // Yo'nalish bor, lekin ustun yo'q bo'lsa saralash ma'nosiz — ikkalasi
    // birga bo'lgandagina qabul qilinadi
    sortField: sortField && (dir === 'ascend' || dir === 'descend') ? sortField : null,
    sortOrder: sortField && (dir === 'ascend' || dir === 'descend') ? dir : null,
    filters,
    search: params.get(names.search) ?? '',
  }
}

/**
 * Holat → URL. Faqat STANDARTDAN FARQ QILADIGAN qiymatlar yoziladi —
 * shunda toza ro'yxat manzili `/subscribers` bo'lib qoladi, `?page=1&size=25`
 * kabi shovqinsiz.
 */
export function writeTableState(
  params: URLSearchParams,
  prefix: string,
  state: TableState,
  defaultPageSize: number,
): URLSearchParams {
  const names = paramNames(prefix)
  const next = new URLSearchParams(params)

  // Eski filtr kalitlari qolib ketmasin
  for (const key of [...next.keys()]) {
    if (key.startsWith(names.filterPrefix)) next.delete(key)
  }

  const setOrDelete = (name: string, value: string | null) => {
    if (value === null || value === '') next.delete(name)
    else next.set(name, value)
  }

  setOrDelete(names.page, state.page > 1 ? String(state.page) : null)
  setOrDelete(names.size, state.pageSize !== defaultPageSize ? String(state.pageSize) : null)
  setOrDelete(names.sort, state.sortField)
  setOrDelete(names.dir, state.sortOrder)
  setOrDelete(names.search, state.search.trim() || null)

  for (const [column, values] of Object.entries(state.filters)) {
    if (values.length > 0) next.set(`${names.filterPrefix}${column}`, values.join(','))
  }

  return next
}

/**
 * So'rov kaliti uchun barqaror obyekt.
 *
 * `filters` dagi kalitlar tartibi URL'ga qarab o'zgarishi mumkin, TanStack
 * Query esa kalitni tuzilma bo'yicha solishtiradi — tartib o'zgarsa u buni
 * BOSHQA so'rov deb biladi va keshni qayta ishlatmaydi. Shuning uchun
 * saralab beriladi.
 */
export function toQueryParams(state: TableState): Record<string, unknown> {
  const filters: Record<string, readonly string[]> = {}
  for (const key of Object.keys(state.filters).sort()) {
    const value = state.filters[key]
    if (value) filters[key] = value
  }
  return {
    page: state.page,
    pageSize: state.pageSize,
    sortField: state.sortField,
    sortOrder: state.sortOrder,
    search: state.search.trim(),
    filters,
  }
}

/**
 * antd bergan ustun filtrlarini MAVJUD holat USTIGA qo'yadi.
 *
 * <h2>⚠️ NEGA `DataTable` DAN AJRATILGAN</h2>
 * Buni tekshirish uchun birlashtirish CHAQIRIB KO'RILADIGAN bo'lishi
 * kerak; manba matnida naqsh qidirish farqni ko'rmasdi
 * ({@code subscriberListParams} dagi bir xil sabab). Naqsh o'tadigan,
 * lekin xulqi teskari kod yozish oson:
 *
 * <pre>
 *   const next = { ...current }
 *   for (const [key, value] of Object.entries(fromTable)) {
 *     const values = toStringValues(value)
 *     if (values.length > 0) next[key] = values   // `else delete` YO'Q
 *   }
 * </pre>
 *
 * Yuqorida `{ ...current }` JOYIDA, ustun filtrini tozalash esa
 * ISHLAMAY qoladi — eski qiymat har sahifa almashtirilishida qayta
 * tiklanadi.
 *
 * <h2>⚠️ IKKI HOLAT ATAYLAB AJRATILGAN</h2>
 * <ul>
 *   <li>kalit UMUMAN kelmadi — ustun {@code filters} e'lon qilmagan,
 *       ya'ni antd u haqda hech narsa bilmaydi. Bunday filtr (filtr
 *       panelidagi yoki sarlavhadagi tanlov) SAQLANADI;</li>
 *   <li>kalit keldi, lekin bo'sh — foydalanuvchi ustun filtrini
 *       ATAYLAB tozaladi. Bunday filtr O'CHIRILADI.</li>
 * </ul>
 *
 * <p>Ikkovini aralashtirish ikki teskari nuqsonning birini beradi:
 * filtr sahifa almashganda jimgina yo'qoladi YOKI tozalash tugmasi
 * ishlamaydi.
 *
 * @param current   manzildagi joriy filtrlar (yagona haqiqat manbai)
 * @param fromTable antd {@code onChange} bergan ro'yxat; qiymat
 *                  {@code null} yoki bo'sh massiv — tozalangan ustun
 */
export function mergeColumnFilters(
  current: TableState['filters'],
  fromTable: Readonly<Record<string, readonly unknown[] | null>>,
): Record<string, readonly string[]> {
  const next: Record<string, readonly string[]> = { ...current }
  for (const [key, value] of Object.entries(fromTable)) {
    const values = value === null ? [] : value.map((v) => String(v))
    if (values.length > 0) next[key] = values
    else delete next[key]
  }
  return next
}
